'use client';

import { FC } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { getSavingsPlanTransactionDetailsFieldsData } from '../utils/getSavingsPlanTransactionDetailsFieldsData';
import { SavingsPlanDetailsTranslations } from '../utils/getSavingsPlanDetailsTranslations';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { TFunction } from 'i18next';
import {
  TransactionDetailsField,
  TransactionDetailsFieldVariants,
} from '../../../transaction-details';
import { SavingsPlanItemType } from '../../types';

const fieldsContainerStyles = css({
  marginBottom: 'extra_small',
  paddingX: 'medium',
  width: '100%',
});

type SavingsPlanDetailsWrapperProps = {
  savingsPlan: SavingsPlanItemType;
  translations: SavingsPlanDetailsTranslations;
  locale: string;
  currency: string;
};

const SavingsPlanDetailsWrapper: FC<SavingsPlanDetailsWrapperProps> = ({
  savingsPlan,
  translations,
  locale,
  currency,
}) => {
  const { t } = useClientTranslation();

  const transactionDetailsFieldsData =
    getSavingsPlanTransactionDetailsFieldsData({
      translations,
      savingsPlan,
      locale,
      currency,
      translationUtil: t as TFunction,
    });

  return (
    <ul
      className={fieldsContainerStyles}
      role="listbox"
      aria-label="savings plan details"
    >
      {transactionDetailsFieldsData.map((entity) => {
        switch (entity.kind) {
          case 'component': {
            const Component = entity.component;

            return (
              <Component
                key={entity.name}
                label={entity.label}
                className={css({
                  py: 'medium',
                  borderBottomWidth: '1px',
                  borderBottomColor: 'neutrals.divider',
                })}
              />
            );
          }

          case 'date-only':
            return (
              <TransactionDetailsField
                key={entity.name}
                locale={locale}
                data-testid={entity.name}
                label={entity.label}
                value={entity.value}
                variant={TransactionDetailsFieldVariants.TRANSACTION_DATE}
                disabled={entity.disabled}
              />
            );

          case 'text':
            return (
              <TransactionDetailsField
                key={entity.name}
                locale={locale}
                data-testid={entity.name}
                label={entity.label}
                value={entity.value}
                disabled={entity.disabled}
              />
            );
        }
      })}
    </ul>
  );
};

export { SavingsPlanDetailsWrapper };
